import instrumentSchema from './schemas/instrument-schema.json';

export interface BoundingBox {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  polygon?: number[]; // [x1,y1,x2,y2,x3,y3,...]
}

// === P&ID AI Extraction Types ===

export type ExtractionJobStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface ExtractionJob {
  job_id: string;
  doc_id: string;
  status: ExtractionJobStatus;
  timed_out?: boolean;
  openai_response_id?: string;
  openai_batch_id?: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
  result_path?: string;
}

export interface BackgroundExtractionJob extends ExtractionJob {
  attempts: number;
  last_polled_at?: string | null;
}

export interface ExtractionUploadResponse {
  job_id: string;
  doc_id: string;
  status: string;
  message: string;
}

export interface ExtractionStartResponse {
  job_id: string;
  status: string;
  mode: 'background' | 'batch';
  file_count: number;
  message: string;
}

export interface BatchExtractionStatus {
  batch_id: string;
  openai_batch_id: string;
  status: string;
  file_count: number;
  doc_ids: string[];
  created_at: string;
  updated_at?: string;
  completed_count?: number;
  failed_count?: number;
  error?: string;
  timed_out?: boolean;
}

export type DocumentStatus = 'draft' | 'in_progress' | 'completed' | 'needs_review';

export interface PidDocument {
  id: string;
  filename: string;
  original_filename: string;
  uploaded_at: string;
  image_path: string;
  pdf_path?: string | null;
  image_url?: string | null;
  pdf_url?: string | null;
  width?: number | null;
  height?: number | null;
  pnid_no?: string | null;
  pnid_title?: string | null;
  /** Display name of the document's domain; null when no domain resolves. */
  plant?: string | null;
  domain_id?: string | null;
  diagram_type?: 'pid' | 'pfd';
  /** The SME-set class from P&ID Setup; null until an SME sets it. */
  drawing_class?: DrawingClass | null;
  /** The applicable legend from P&ID Setup; null until an SME assigns one. */
  legend_sheet_id?: string | null;
  /** The assigned sheet's name; null when none is assigned or the sheet no longer exists. */
  legend_sheet_name?: string | null;
  deleted?: boolean;
}

/** One domain from GET /domains. */
export interface DomainSummary {
  id: string;
  name: string;
  parent_id: string | null;
}

/** GET /domains groups every domain under its owning account. */
export interface AccountDomains {
  account_id: string;
  account_name: string | null;
  domains: DomainSummary[];
}

/**
 * The `owner_domain_id` for an asset that belongs to no plant the register
 * holds. It is stored inside the annotation like any other owner.
 */
export const OWNER_DOMAIN_OTHERS = '__others__';

/**
 * The plant an asset is owned by. An asset that names no owner belongs to the
 * drawing that draws it, so a picker showing this names the drawing's own plant
 * for an asset no reviewer has given an owner.
 */
export function effectiveOwnerDomainId(
  storedOwnerDomainId: string,
  drawingDomainId: string,
): string {
  return storedOwnerDomainId || drawingDomainId;
}

export type EquipmentLinkStatus = 'confirmed' | 'needs_review';
export type EquipmentLinkSource = 'exact_tag' | 'normalized_tag' | 'manual';
export type OpcHyperlinkStatus = 'confirmed' | 'needs_review';
export type OpcHyperlinkSource = 'manual' | 'exact_line' | 'ocr_line_match' | 'high_similarity';
export type PfdStreamLinkStatus = 'confirmed' | 'needs_review';
export type PfdStreamLinkSource = 'exact_stream' | 'manual';
export type PfdStreamEndpointRole = 'source_pnid' | 'dest_pnid';
export type PfdStreamDirection = 'source_to_dest' | 'dest_to_source';
export type DocumentReviewCorrectionType =
  | 'value'
  | 'mapping'
  | 'support_mapping'
  | 'classification'
  | 'extraction'
  | 'missing'
  | 'supporting_ref_add'
  | 'supporting_ref_remove'
  | 'supporting_ref_replace'
  | 'review_confirmed';
export type DocumentReviewDecision = 'confirmed' | 'rejected' | 'corrected' | 'flagged';

export interface LinkedPnidRef {
  pnid_file_id: string;
  equipment_id: string;
  status: EquipmentLinkStatus;
  source: EquipmentLinkSource;
}

export interface LinkedPfdRef {
  pfd_file_id: string;
  equipment_id: string;
  status: EquipmentLinkStatus;
  source: EquipmentLinkSource;
}

export interface DocumentReviewCorrectionCreate {
  target_ref?: string | null;
  correction_type: DocumentReviewCorrectionType;
  decision: DocumentReviewDecision;
  evidence_source_file_id?: string | null;
  evidence_ref?: Record<string, any>;
  correction: Record<string, any>;
  note?: string | null;
}

export interface DocumentReviewCorrectionRecord extends DocumentReviewCorrectionCreate {
  id: string;
  source_file_id: string;
  created_by: string;
  created_at: string;
}

// Annotation info from pnid_annotations table
export interface AnnotationInfo {
  id: string;
  file_id?: string;
  user: string | null;
  status: DocumentStatus | null;
  created_at?: string;
  updated_at?: string;
}

// Combined document with annotation info for UI display
export interface DocumentWithAnnotation {
  document: PidDocument;
  annotation: AnnotationInfo | null;
}

// === Isolation Planning ===

export interface IsolationPnidOption {
  doc_id: string;
  pnid_no: string;
}

export interface IsolationEquipmentOption {
  equipment_no: string;
  equipment_type: string;
  description: string;
}

export type IsolationActionType = 'CLOSE' | 'OPEN' | 'NOTE';
export type IsolationActionSource = 'graph' | 'relationship' | 'tag_reference' | 'instrument_attachment';

export interface IsolationAction {
  step_number: number;
  instrument_id: string;
  instrument_ref: string;
  instrument_type: string;
  action: IsolationActionType;
  line_no: string | null;
  reasoning: string;
  source: IsolationActionSource;
  highlightable: boolean;
  evidence_ref: string | null;
  bbox: BoundingBox | null;
}

export interface IsolationPath {
  line_no: string;
  from_ref: string;
  to_ref: string;
  actions: IsolationAction[];
  has_isolation: boolean;
}

export interface EquipmentIsolationAction {
  equipment_no: string;
  equipment_type: string;
  description: string;
  action: string;
  reasoning: string;
  is_driver: boolean;
  driven_equipment_no: string | null;
  bbox: BoundingBox | null;
}

export interface IsolationPlan {
  equipment_no: string;
  pnid_no: string;
  paths: IsolationPath[];
  equipment_actions: IsolationAction[];
  equipment_isolations: EquipmentIsolationAction[];
  warnings: string[];
}

export interface Section {
  id: string;
  doc_id?: string;
  bbox: BoundingBox;
  context?: string;
  status: string;
  created_at?: string;
  processed_at?: string | null;
  error_message?: string;
}

// === Marking Status ===
// 5-state status tracking:
// ai_identified -> in_progress/final -> final_and_reviewed (or skipped)
export type MarkingStatus = 'ai_identified' | 'in_progress' | 'final' | 'final_and_reviewed' | 'skipped';

// Visual styles for marking status on canvas
export interface MarkingStatusStyle {
  color: string;
  dash: number[] | null;  // null = solid line
  fill: string;
  label: string;
}

export const MARKING_STATUS_STYLES: Record<MarkingStatus, MarkingStatusStyle> = {
  ai_identified: { 
    color: '#3b82f6',  // Blue
    dash: [4, 4],      // Dotted
    fill: 'rgba(59, 130, 246, 0.1)',
    label: 'AI Identified' 
  },
  in_progress: { 
    color: '#f59e0b',  // Orange
    dash: [8, 4],      // Dashed
    fill: 'rgba(245, 158, 11, 0.1)',
    label: 'In Progress' 
  },
  final: { 
    color: '#d97706',  // Amber (not reviewed yet)
    dash: null,        // Solid
    fill: 'rgba(217, 119, 6, 0.12)',
    label: 'Final' 
  },
  final_and_reviewed: {
    color: '#22c55e',  // Green
    dash: null,        // Solid
    fill: 'rgba(34, 197, 94, 0.1)',
    label: 'Reviewed' 
  },
  skipped: { 
    color: '#8b5cf6',  // Purple
    dash: [2, 6],      // Sparse dots
    fill: 'rgba(139, 92, 246, 0.1)',
    label: 'Skipped' 
  },
};

export function getMarkingStatusStyle(status: MarkingStatus): MarkingStatusStyle {
  return MARKING_STATUS_STYLES[status] || MARKING_STATUS_STYLES.ai_identified;
}

// === Status Computation Helpers ===
// Determine marking status based on field completeness

export type ItemType = 'equipment' | 'equipment_instrument' | 'node' | 'node_instrument' | 'untagged_instrument' | 'control_loop';

// Check if equipment has all required fields
function isEquipmentComplete(item: Record<string, any>): boolean {
  return Boolean(
    item.equipment_no?.trim() &&
    item.equipment_type?.trim() &&
    item.description?.trim() &&
    item.bbox
  );
}

// Check if equipment instrument has all required fields
function isEquipmentInstrumentComplete(item: Record<string, any>): boolean {
  return Boolean(
    item.instrument_type &&
    item.instrument_text?.trim() &&
    item.bbox
  );
}

// Check if node has all required fields
function isNodeComplete(item: Record<string, any>): boolean {
  const hasIdentifier = Boolean(item.line_no?.trim() || item.node_no);
  const hasReference = Boolean(
    (item.source_ref?.trim() || item.source_pnid?.trim()) ||
    (item.dest_ref?.trim() || item.dest_pnid?.trim())
  );
  const hasGeometry = item.is_duct
    ? Boolean(item.duct_bbox)
    : Boolean(item.polyline && item.polyline.length >= 4);
  return hasIdentifier && hasReference && hasGeometry;
}

// Check if node instrument has all required fields  
function isNodeInstrumentComplete(item: Record<string, any>): boolean {
  return Boolean(
    item.instrument_text?.trim() &&
    item.bbox
  );
}

// Check if untagged instrument has all required fields
function isUntaggedInstrumentComplete(item: Record<string, any>): boolean {
  return Boolean(
    item.instrument_type &&
    item.bbox
  );
}

// Check if control loop has all required fields
function isControlLoopComplete(item: Record<string, any>): boolean {
  return Boolean(
    item.tag?.trim() &&
    item.loop_type?.trim() &&
    item.bbox
  );
}

/**
 * Compute the marking status for an item based on its fields.
 * - ai_identified: Has no bbox (from AI extraction, not marked)
 * - in_progress: Has bbox but missing required fields
 * - final: Has bbox and all required fields complete
 */
export function computeMarkingStatus(item: Record<string, any>, itemType: ItemType): MarkingStatus {
  // Check if item has bbox (for most types) or polyline (for nodes)
  const hasBbox = Boolean(item.bbox);
  const hasPolyline = Boolean(item.polyline && item.polyline.length >= 4);
  const hasNodeGeometry = item.is_duct ? Boolean(item.duct_bbox) : hasPolyline;
  const isMarked = itemType === 'node' ? hasNodeGeometry : hasBbox;
  
  // Not marked yet = AI identified
  if (!isMarked) {
    return 'ai_identified';
  }
  
  // Check completeness based on type
  let isComplete = false;
  switch (itemType) {
    case 'equipment':
      isComplete = isEquipmentComplete(item);
      break;
    case 'equipment_instrument':
      isComplete = isEquipmentInstrumentComplete(item);
      break;
    case 'node':
      isComplete = isNodeComplete(item);
      break;
    case 'node_instrument':
      isComplete = isNodeInstrumentComplete(item);
      break;
    case 'untagged_instrument':
      isComplete = isUntaggedInstrumentComplete(item);
      break;
    case 'control_loop':
      isComplete = isControlLoopComplete(item);
      break;
  }
  
  return isComplete ? 'final' : 'in_progress';
}

// === P&ID Wizard Types ===

export type NodePointCandidateStatus = 'running' | 'ready' | 'error';

export type NodePointCandidateLabel = InstrumentCategory | 'Branch';

export interface NodePointCandidateOption {
  label: NodePointCandidateLabel;
  score: number;
}

export interface NodePointCandidate {
  id: string;
  nodeId: string;
  point: { x: number; y: number };
  bbox?: BoundingBox | null;
  status: NodePointCandidateStatus;
  options: NodePointCandidateOption[];
  identifiedText?: string | null;
  symbolMatch?: string | null;
  tagText?: string | null;
  confidence?: string | null;
  error?: string;
  source?: 'classification' | 'branch';
  branchRef?: string | null;
  branchLineNo?: string | null;
  branchDirection?: 'IN' | 'OUT' | null;
  branchLabel?: string | null;
  branchAngleDeg?: number | null;
  branchDistancePx?: number | null;
}

export interface PnidWizardMeta {
  schema_version: number;
  created_at: string;
  updated_at: string;
}

export type EquipmentInstrumentType =
  | 'PTs' | 'TTs' | 'TE' | 'LTs' | 'FT'
  | 'PG' | 'TG' | 'LG' | 'TW' | 'FE' | 'RO'
  | 'PI' | 'TI' | 'LI' | 'FI'
  | 'HIC' | 'LIC' | 'PIC' | 'TIC' | 'FIC' | 'PY' | 'TY' | 'LY' | 'FY' | 'HY'
  | 'AI'
  | 'AE' | 'AT' | 'SSE'
  | 'Vibration Instruments'
  | 'Speed Instruments'
  | 'Current Instruments'
  | 'Flame Scanner'
  | 'Safety Valve'
  // modulates flow under a controller signal, unlike the isolation 'Valve'
  | 'Control Valve'
  // an isolation valve in SME usage: a valve body that blocks flow, not one that modulates it
  | 'Valve'
  | 'Solenoid Valve' | 'Valve with Blind Flange' | 'Valve with Spectacle Blind' | 'Diaphragm Seal' | 'Pulsation Dampener' | 'Drain' | 'vents'
  | 'Man hole' | 'Hand hole'
  | 'Pipe Fitting'
  | 'Utility Connection'
  | 'Nozzle'
  | 'OPC'
  | 'Other';

export type LineInstrumentCategory =
  // modulates flow under a controller signal, unlike the isolation 'Valve'
  | 'Control Valve'
  // an isolation valve in SME usage: a valve body that blocks flow, not one that modulates it
  | 'Valve'
  | 'Solenoid Valve' | 'PG' | 'TG' | 'TE' | 'LG' | 'TW' | 'FE' | 'RO'
  | 'HIC' | 'LIC' | 'PIC' | 'TIC' | 'FIC' | 'PY' | 'TY' | 'LY' | 'FY' | 'HY'
  | 'AI'
  | 'Switch/Alarm' | 'Control System' | 'Interlock' | 'Sampling Point'
  | 'Pipe Fitting' | 'ST' | 'Check Valve' | 'Orifice Plate'
  | 'OPC' | 'Other'
  | 'Branch';

export type InstrumentCategory = EquipmentInstrumentType | LineInstrumentCategory;

export const EQUIPMENT_INSTRUMENT_NOMENCLATURE = instrumentSchema.equipment_instrument_types as Record<EquipmentInstrumentType, string>;

export const LINE_INSTRUMENT_NOMENCLATURE = instrumentSchema.line_instrument_types as Record<LineInstrumentCategory, string>;
export const TAGGED_INSTRUMENT_NOMENCLATURE: Record<InstrumentCategory, string> = {
  ...EQUIPMENT_INSTRUMENT_NOMENCLATURE,
  ...LINE_INSTRUMENT_NOMENCLATURE,
};

export const INSTRUMENT_CATEGORY_LABELS: Record<InstrumentCategory, string> = {
  ...TAGGED_INSTRUMENT_NOMENCLATURE,
};

export const EQUIPMENT_INSTRUMENT_CATEGORIES = Object.keys(EQUIPMENT_INSTRUMENT_NOMENCLATURE) as EquipmentInstrumentType[];

export const LINE_INSTRUMENT_CATEGORIES = Object.keys(LINE_INSTRUMENT_NOMENCLATURE) as LineInstrumentCategory[];
export const TAGGED_INSTRUMENT_CATEGORIES = Array.from(
  new Set<InstrumentCategory>([
    ...EQUIPMENT_INSTRUMENT_CATEGORIES,
    ...LINE_INSTRUMENT_CATEGORIES,
  ]),
) as InstrumentCategory[];

// A stored instrument type that is not itself a schema key maps here to the key that owns
// it, so one schema key, one option list and one visibility rule serve every spelling a row
// carries. The schema names the strainer type `ST`; rows spell it `Strainer`.
// The backend applies the same table in `canonicalize_instrument_type`
// (backend/models/pnid_schema.py).
const INSTRUMENT_TYPE_ALIASES: Record<string, string> = {
  Strainer: 'ST',
};

export function canonicalizeInstrumentType(code: string | null | undefined): string {
  if (!code) return '';
  return INSTRUMENT_TYPE_ALIASES[code] ?? code;
}

export function getEquipmentInstrumentName(code: string | null | undefined): string {
  const canonicalCode = canonicalizeInstrumentType(code);
  if (!canonicalCode) return '';
  return (EQUIPMENT_INSTRUMENT_NOMENCLATURE as Record<string, string>)[canonicalCode] || canonicalCode;
}

export function getInstrumentCategoryName(code: string | null | undefined): string {
  const canonicalCode = canonicalizeInstrumentType(code);
  if (!canonicalCode) return '';
  return (INSTRUMENT_CATEGORY_LABELS as Record<string, string>)[canonicalCode] || canonicalCode;
}

// === Contextual Valve Types ===
// Same valve symbol can represent different contextual purposes

export type ContextualValveType =
  | 'IV'    // Isolation Valve (generic)
  | 'NRV'   // Non-Return Valve (Check Valve)
  | 'DV'    // Drain Valve
  | 'LPD'   // Low Point Drain
  | 'VV'    // Vent Valve
  | 'HPV'   // High Point Vent
  | 'BV'    // Bypass Valve
  | 'BK'    // Block Valve
  | 'BL'    // Bleed Valve
  | 'RV';   // Relief Valve

export type LinkedInstrumentConnectionType =
  | 'Connected to Process'
  | 'Electrical'
  | 'Pneumatic'
  | 'Capillary'
  | 'Hydraulic'
  | 'Software'
  | 'Electromagnetic or Sonic';

export const LINKED_INSTRUMENT_CONNECTION_TYPES =
  instrumentSchema.linked_instrument_connection_types as LinkedInstrumentConnectionType[];

export interface PnidLinkedInstrumentLink {
  instrument_id: string | null;
  instrument_ref: string;
  connection_type: LinkedInstrumentConnectionType;
}

export interface PnidHyperlinkRef {
  pnid_doc_id: string | null;
  pnid_no: string;
  target_node_id?: string | null;
  target_opc_instrument_id?: string | null;
  target_opc_tag?: string | null;
  target_line_no_snapshot?: string | null;
  status?: OpcHyperlinkStatus;
  source?: OpcHyperlinkSource;
}

// Flat nomenclature for contextual types
export const CONTEXTUAL_TYPE_NOMENCLATURE = instrumentSchema.contextual_valve_types as Record<ContextualValveType, string>;

// Grouped for UI display: {group_name: {code: label}}
export const CONTEXTUAL_TYPE_GROUPS: Record<string, Partial<Record<ContextualValveType, string>>> = Object.fromEntries(
  Object.entries(instrumentSchema.contextual_type_groups as Record<string, string[]>).map(([group, codes]) => [
    group,
    Object.fromEntries(codes.map((c) => [c, CONTEXTUAL_TYPE_NOMENCLATURE[c as ContextualValveType]])),
  ])
);

export function getContextualTypeName(code: ContextualValveType): string {
  return CONTEXTUAL_TYPE_NOMENCLATURE[code] || code;
}

// Equipment-side instrument types that show the contextual type selector.
// Derived from visibility rules, filtered to those that appear as equipment types.
export const CONTEXTUAL_TYPE_APPLICABLE: EquipmentInstrumentType[] =
  (instrumentSchema.visibility_rules.contextual_type_applicable_types as string[])
    .filter((t) => t in EQUIPMENT_INSTRUMENT_NOMENCLATURE) as EquipmentInstrumentType[];

// === Instrument Subtypes ===
// Based on P&ID legend CSV - specific valve/instrument body types

export const SUBTYPE_MAPPING: Record<string, string[]> = instrumentSchema.subtype_mapping;

export const UNTAGGED_SUBTYPE_MAPPING: Record<string, string[]> = instrumentSchema.untagged_subtype_mapping;

export function getSubtypesForType(instrumentType: string): string[] {
  return SUBTYPE_MAPPING[canonicalizeInstrumentType(instrumentType)] || [];
}

export function getSubtypesForUntaggedType(untaggedType: string): string[] {
  return UNTAGGED_SUBTYPE_MAPPING[untaggedType] || [];
}

export interface PnidEquipment {
  id: string;
  equipment_no: string;
  equipment_type: string;
  /** The sub-type label as the vocabulary serves it under `equipment_type`.
   *  Null when the reviewer picked none. */
  subtype?: string | null;
  description: string;
  pnid_no?: string | null;
  bbox?: BoundingBox | null;
  // Bounding box of equipment tag label from AI (for navigation to unmarked equipment)
  label_bbox?: BoundingBox | null;
  status: MarkingStatus;
  details: Record<string, any>;
  ai_instruments: string[];
  notes?: string | null;
  painting_spec?: string | null;
  related_docs?: Array<{
    id: string;
    name: string;
    type: string;
    url?: string | null;
    domain_id?: string | null;
  }>;
  equipment_data?: Record<string, any> | null;
  supporting_refs?: Record<string, any>[] | null;
  connected_equipments?: Array<{ equipment_id: string; role: string }> | null;
  linked_pnids?: LinkedPnidRef[] | null;
  linked_pfds?: LinkedPfdRef[] | null;
  drawing_note_refs?: PnidDrawingNoteRef[] | null;
  /**
   * Marks this drawing as the authoritative one for the asset. Only the primary
   * drawing may edit the asset's canonical fields; every other appearance is
   * evidence. Undefined means the drawing never carried the mark.
   */
  is_primary_source?: boolean | null;
  /**
   * The plant domain a reviewer named as this asset's owner. Empty means the
   * drawing that draws the asset owns it.
   */
  owner_domain_id: string;
  provenance?: MarkingProvenance | null;
}

export type TextEnrichmentReviewStatus = 'proposed' | 'approved' | 'rejected';

export interface TextEnrichmentReviewRecord {
  status: TextEnrichmentReviewStatus;
  version: number;
  approved_against?: {
    content_hash: string;
    document_revision?: string | null;
  } | null;
}

export interface TextEnrichmentFact {
  key: string;
  source_label: string;
  value: {
    raw?: string | null;
    normalized?: Record<string, unknown> | null;
    unit?: string | null;
  };
  override?: {
    raw?: string | null;
    normalized?: Record<string, unknown> | null;
    unit?: string | null;
    reason?: string | null;
  } | null;
  review: TextEnrichmentReviewRecord;
}

export interface PnidDrawingNote {
  id: string;
  label: string;
  text: string;
  override_text?: string | null;
  review: TextEnrichmentReviewRecord;
}

export interface PnidDrawingNoteRef {
  note_id: string;
  status: TextEnrichmentReviewStatus;
  confidence?: number | null;
  reason?: string | null;
}

export interface PnidRevisionRow {
  id: string;
  rev: string;
  fields: Record<string, TextEnrichmentFact>;
  review: TextEnrichmentReviewRecord;
}

export interface PnidTextEnrichment {
  v: number;
  key_registry_version: string;
  notes_by_id: Record<string, PnidDrawingNote>;
  doc_meta: Record<string, TextEnrichmentFact>;
  revisions: PnidRevisionRow[];
}

// === Drain Types ===
// For nodes with drain as destination (from Legend CSV)

export const DRAIN_TYPES: Record<string, string> = {
  'DH': 'Hydrocarbon Drain',
  'DR': 'General Drain',
  'OS': 'Oily Sewer',
  'CS': 'Chemical Sewer',
  'AS': 'AS Solution Sewer',
  'US': 'Urea Solution Sewer',
  'SW': 'Storm Water Sewer',
};

export function getDrainTypeName(code: string): string {
  return DRAIN_TYPES[code] || code;
}

// Keywords to detect drain destinations
export function isDrainDestination(destRef: string): boolean {
  const lower = destRef.toLowerCase();
  return lower.includes('drain') || lower.includes('sewer') || lower.includes('pit');
}

export type LineType = 'process' | 'instrument' | 'tapping';

export const LINE_TYPE_LABELS: Record<LineType, string> = {
  process: 'Process Line',
  instrument: 'Instrument Line',
  tapping: 'Tapping Line',
};
export type InstrumentLineSubtype =
  | 'electrical'
  | 'pneumatic'
  | 'capillary'
  | 'hydraulic'
  | 'software'
  | 'electromagnetic_or_sonic';

export const INSTRUMENT_LINE_SUBTYPE_LABELS: Record<InstrumentLineSubtype, string> = {
  electrical: 'Electrical',
  pneumatic: 'Pneumatic',
  capillary: 'Capillary',
  hydraulic: 'Hydraulic',
  software: 'Software',
  electromagnetic_or_sonic: 'Electromagnetic or Sonic',
};

export type InstallationType =
  | 'above_ground_main'
  | 'above_ground_sub'
  | 'underground_buried'
  | 'intermittent_use';
export const INSTALLATION_TYPE_LABELS: Record<InstallationType, string> = {
  above_ground_main: 'Above Ground (Main)',
  above_ground_sub: 'Above Ground (Sub)',
  underground_buried: 'Underground / Buried',
  intermittent_use: 'Intermittent Use Pipe Line',
};

export type TracingType =
  | 'none'
  | 'steam_traced'
  | 'steam_traced_winterizing'
  | 'electric_traced'
  | 'heat_traced';
export const TRACING_TYPE_LABELS: Record<TracingType, string> = {
  none: 'None',
  steam_traced: 'Steam Traced',
  steam_traced_winterizing: 'Steam Traced (Winterizing)',
  electric_traced: 'Electric Traced',
  heat_traced: 'Heat Traced',
};

export type FlowProfile =
  | 'standard'
  | 'gravity_flow'
  | 'free_draining'
  | 'sloped'
  | 'no_liquid_pockets'
  | 'no_vapor_pockets'
  | 'normally_no_flow';
export const FLOW_PROFILE_LABELS: Record<FlowProfile, string> = {
  standard: 'Standard',
  gravity_flow: 'Gravity Flow',
  free_draining: 'Free Draining',
  sloped: 'Sloped',
  no_liquid_pockets: 'No Liquid Pockets',
  no_vapor_pockets: 'No Vapor Pockets',
  normally_no_flow: 'Normally No Flow (NNF)',
};

export type FlowDirection = 'forward' | 'bidirectional' | 'bidirectional_primary';
export const FLOW_DIRECTION_LABELS: Record<FlowDirection, string> = {
  forward: 'Forward',
  bidirectional: 'Bidirectional',
  bidirectional_primary: 'Bidirectional (Primary Direction)',
};

export type PrimaryDirection = 'source_to_dest' | 'dest_to_source';
export const PRIMARY_DIRECTION_LABELS: Record<PrimaryDirection, string> = {
  source_to_dest: 'Source → Destination',
  dest_to_source: 'Destination → Source',
};

export interface PnidNode {
  id: string;
  node_no: number;
  source_ref: string;
  source_pnid: string;
  /** The id of the entity the source end binds to; null when the ref is text alone (D8). */
  source_id?: string | null;
  to?: string | null;
  dest_ref: string;
  dest_pnid: string;
  dest_id?: string | null;
  line_no: string;
  note?: string | null;
  line_type?: LineType;
  instrument_line_subtype?: InstrumentLineSubtype | null;
  installation_type?: InstallationType | null;
  is_jacketed?: boolean;
  tracing_type?: TracingType | null;
  flow_profile?: FlowProfile | null;
  slope_ratio?: string | null;
  is_two_phase?: boolean;
  flow_direction?: FlowDirection | null;
  primary_direction?: PrimaryDirection | null;
  drain_type?: string | null;
  painting_spec?: string | null;
  is_duct?: boolean;
  duct_bbox?: BoundingBox | null;
  polyline?: number[] | null;
  status: MarkingStatus;
  line_spec: Record<string, any>;
  notes?: string | null;
  parent_node_id?: string | null;
  branch_id?: string | null;
  is_bifurcation_point?: boolean;
  // Line segment review (AI-derived)
  line_segments?: PnidNodeLineSegment[];
  linked_pfd_streams?: PnidLinkedPfdStreamRef[] | null;
  segments_review_status?: SegmentsReviewStatus;
  // Bounding box of line number label from AI (for navigation to unmarked lines)
  label_bbox?: BoundingBox | null;
  supporting_refs?: Record<string, any>[] | null;
  provenance?: MarkingProvenance | null;
}

export type LineSegmentStatus = 'pending' | 'confirmed' | 'edited' | 'deleted';
export type SegmentsReviewStatus = 'pending' | 'in_review' | 'confirmed';

export interface PnidNodeLineSegment {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  kind?: string | null;
  confidence?: number | null;
  status: LineSegmentStatus;
}

export interface PnidLinkedPfdStreamRef {
  pfd_file_id: string;
  pfd_no?: string | null;
  node_id: string;
  source_line_no_snapshot?: string | null;
  target_line_no_snapshot?: string | null;
  source_opc_instrument_id?: string | null;
  source_opc_tag?: string | null;
  target_opc_instrument_id?: string | null;
  target_opc_tag?: string | null;
  endpoint_role: PfdStreamEndpointRole;
  direction: PfdStreamDirection;
  status: PfdStreamLinkStatus;
  source: PfdStreamLinkSource;
}

// Branch direction for node instruments (IN = joining, OUT = splitting)
export type BranchDirection = 'IN' | 'OUT';

// === Untagged Instruments ===

export type UntaggedInstrumentType =
  | 'IV'   // Isolation Valve
  | 'NRV'  // Non-Return Valve (Check Valve)
  | 'DV'   // Drain Valve
  | 'LPD'  // Low Point Drain
  | 'VV'   // Vent Valve
  | 'HPV'  // High Point Vent
  | 'BV'   // Bypass Valve
  | 'BK'   // Block Valve
  | 'BL'   // Bleed Valve
  | 'RD'   // Reducer
  | 'EX'   // Expander
  | 'SP'   // Spectacle Blind
  | 'PF'   // Pipe Fitting (Blind Flange, Flange, Elbow, Tee, Cap, Union, etc.)
  | 'SS'   // Steam Trap
  | 'FA'   // Flame Arrester
  | 'SG'   // Sight Glass
  | 'OT';  // Other

// Nomenclature mapping: code -> full name
export const UNTAGGED_INSTRUMENT_NOMENCLATURE = instrumentSchema.untagged_instrument_types as Record<UntaggedInstrumentType, string>;

// Get the full name for an untagged instrument type code
export function getUntaggedInstrumentName(code: UntaggedInstrumentType): string {
  return UNTAGGED_INSTRUMENT_NOMENCLATURE[canonicalizeInstrumentType(code) as UntaggedInstrumentType] || code;
}

// === Instrument Relationships ===

export type RelationshipType =
  | 'isolation'   // Isolation valve for instrument
  | 'root_valve'  // Root/first block valve
  | 'drain'       // Drain valve
  | 'vent'        // Vent valve
  | 'bypass'      // Bypass valve
  | 'block_1'     // First block (block & bleed)
  | 'block_2'     // Second block
  | 'bleed'       // Bleed valve
  | 'controls'    // Controller -> Control valve
  | 'measures'    // Transmitter -> Controller
  | 'trips';      // Switch -> Shutdown valve

export type InstrumentRefType = 'tagged' | 'untagged' | 'equipment_instrument' | 'node_instrument';

export interface PnidInstrumentRelationship {
  id: string;
  relationship_type: RelationshipType;
  from_type: InstrumentRefType;
  from_id: string;
  from_ref: string;
  to_type: InstrumentRefType;
  to_id: string;
  to_ref: string;
  auto_generated: boolean;
}

export interface PnidInstrumentRelationshipCreate {
  relationship_type: RelationshipType;
  from_type: InstrumentRefType;
  from_id: string;
  from_ref: string;
  to_type: InstrumentRefType;
  to_id: string;
  to_ref: string;
  auto_generated?: boolean;
}

// === Control Loops ===

export type ControlledVariableType = 'flow' | 'pressure' | 'level' | 'temperature' | 'analytical' | 'other';

export interface PnidControlLoop {
  id: string;
  loop_tag: string;
  members: string[];
  controlled_variable?: ControlledVariableType | null;
  controlled_element?: string | null;
  controlled_object?: string | null;
  controller_type?: string | null;
  bbox?: BoundingBox | null;
  status: MarkingStatus;
  notes?: string | null;
}

export interface PnidControlLoopCreate {
  loop_tag: string;
  members?: string[];
  controlled_variable?: ControlledVariableType | null;
  controlled_element?: string | null;
  controlled_object?: string | null;
  controller_type?: string | null;
  bbox?: BoundingBox | null;
  status?: MarkingStatus;
  notes?: string | null;
}

export interface PnidControlLoopUpdate {
  loop_tag?: string;
  members?: string[];
  controlled_variable?: ControlledVariableType | null;
  controlled_element?: string | null;
  controlled_object?: string | null;
  controller_type?: string | null;
  bbox?: BoundingBox | null;
  status?: MarkingStatus;
  notes?: string | null;
}

export type DrawingClass = 'process' | 'utility' | 'vendor' | 'package' | 'interconnection';

export interface PnidSetupUpdate {
  /** Selected legend record in the drawing's plant. */
  legend_sheet_id?: string | null;
  pnid_no: string;
  pnid_title?: string;
  drawing_class?: DrawingClass | null;
}

export interface PnidWizardState {
  /** Selected legend record in the drawing's plant. */
  legend_sheet_id?: string | null;
  doc_id: string;
  pnid_no?: string | null;
  pnid_title?: string | null;
  drawing_class?: DrawingClass | null;
  meta: PnidWizardMeta;
  equipments: PnidEquipment[];
  instruments: PnidInstrument[];
  nodes: PnidNode[];
  control_loops: PnidControlLoop[];
  instrument_relationships: PnidInstrumentRelationship[];
  ai_data_loaded: boolean;
  ai_metadata?: {
    drawing_metadata?: Record<string, any> | null;
    notes?: any[];
    external_equipment_refs?: any[];
    tie_ins?: any[];
    source?: Record<string, any> | null;
    revision_history?: any[];
    supporting_data?: Record<string, any> | null;
  } | null;
  text_enrichment?: PnidTextEnrichment | null;
}

/** What a finding asks for. `BLOCKER`: the drawing contradicts the data and the fix can be
 *  computed. `FLAG`: a person must supply a fact. `ADVISORY` has no rule yet. */
export type Severity = 'BLOCKER' | 'FLAG' | 'ADVISORY';

/** The `PnidWizardState` collection an element is in: a node is a line or a duct. */
export type ElementKind = 'instrument' | 'node' | 'equipment';

/** The element's data field a rule judges, which an editor shows the finding under: `context` is
 *  an instrument's parent, `set_pressure`, `mounting_type`, `pnid_hyperlink` and
 *  `instrument_text` (its tag) are instrument fields, `equipment_no` is an equipment's tag, and
 *  `instruments` is a line's instruments and their order. Mirrors the backend's `RuleField`. */
export type FindingField =
  | 'context'
  | 'set_pressure'
  | 'mounting_type'
  | 'pnid_hyperlink'
  | 'instruments'
  | 'instrument_text'
  | 'equipment_no';

/** One broken rule on one element. It is always read under the element it belongs to. */
export interface Finding {
  /** The rule id, `ATTACH-001`. */
  rule: string;
  severity: Severity;
  field: FindingField;
  /** One sentence that names elements by their ref, never by id. */
  message: string;
  /** The other elements the finding is about. */
  related_ids: string[];
}

export interface ElementValidation {
  kind: ElementKind;
  findings: Finding[];
}

export interface DrawingValidation {
  /** Keyed by element id. An element with no finding has no entry. */
  elements: Record<string, ElementValidation>;
}

/** A drawing on which no rule finds anything. */
export const NO_FINDINGS: DrawingValidation = { elements: {} };

/** The drawing GET: the stored state, and the findings the server computes on each read.
 *  `validation` is never stored, so a writer sends back `state` alone. */
export interface PnidStateResponse {
  state: PnidWizardState;
  validation: DrawingValidation;
}

/** The instrument GET: the stored instrument and its findings. */
export interface PnidInstrumentResponse {
  instrument: PnidInstrument;
  validation: ElementValidation;
}

/** A validate-draft answer: the findings the drawing would hold if Save sent the same body. */
export interface DraftValidation {
  /** The edited element's findings. */
  element: ElementValidation;
  /** Keyed by element id: every other element whose findings the body changes. An element
   *  whose findings all clear is present with no finding. */
  changed: Record<string, ElementValidation>;
}

// Create/Update interfaces for API calls
export interface PnidEquipmentCreate {
  equipment_no: string;
  equipment_type: string;
  subtype?: string | null;
  description: string;
  pnid_no?: string | null;
  bbox?: BoundingBox | null;
  label_bbox?: BoundingBox | null;
  status?: MarkingStatus;
  details?: Record<string, any>;
  ai_instruments?: string[];
  notes?: string | null;
  painting_spec?: string | null;
  related_docs?: Array<{
    id: string;
    name: string;
    type: string;
    url?: string | null;
    domain_id?: string | null;
  }>;
  equipment_data?: Record<string, any> | null;
  connected_equipments?: Array<{ equipment_id: string; role: string }> | null;
  linked_pnids?: LinkedPnidRef[] | null;
  linked_pfds?: LinkedPfdRef[] | null;
  is_primary_source?: boolean | null;
  /**
   * The plant domain a reviewer named as this asset's owner. Empty means the
   * drawing that draws the asset owns it; absent says nothing about ownership.
   */
  owner_domain_id?: string;
}

export interface PnidEquipmentUpdate {
  equipment_no?: string;
  equipment_type?: string;
  /** Null clears the stored sub-type; an absent key leaves it alone. */
  subtype?: string | null;
  description?: string;
  pnid_no?: string | null;
  bbox?: BoundingBox | null;
  label_bbox?: BoundingBox | null;
  status?: MarkingStatus;
  details?: Record<string, any>;
  ai_instruments?: string[];
  notes?: string | null;
  painting_spec?: string | null;
  related_docs?: Array<{
    id: string;
    name: string;
    type: string;
    url?: string | null;
    domain_id?: string | null;
  }>;
  equipment_data?: Record<string, any> | null;
  connected_equipments?: Array<{ equipment_id: string; role: string }> | null;
  linked_pnids?: LinkedPnidRef[] | null;
  linked_pfds?: LinkedPfdRef[] | null;
  is_primary_source?: boolean | null;
  /**
   * The plant domain a reviewer named as this asset's owner. Empty means the
   * drawing that draws the asset owns it; absent says nothing about ownership.
   */
  owner_domain_id?: string;
}

export interface PnidNodeCreate {
  node_no?: number | null;
  source_ref?: string;
  source_pnid?: string;
  to?: string | null;
  dest_ref?: string;
  dest_pnid?: string;
  line_no: string;
  note?: string | null;
  line_type?: LineType;
  instrument_line_subtype?: InstrumentLineSubtype | null;
  installation_type?: InstallationType | null;
  is_jacketed?: boolean;
  tracing_type?: TracingType | null;
  flow_profile?: FlowProfile | null;
  slope_ratio?: string | null;
  is_two_phase?: boolean;
  flow_direction?: FlowDirection | null;
  primary_direction?: PrimaryDirection | null;
  drain_type?: string | null;
  painting_spec?: string | null;
  is_duct?: boolean;
  duct_bbox?: BoundingBox | null;
  polyline?: number[] | null;
  status?: MarkingStatus;
  line_spec?: Record<string, any>;
  notes?: string | null;
  parent_node_id?: string | null;
  branch_id?: string | null;
  is_bifurcation_point?: boolean;
  line_segments?: PnidNodeLineSegment[] | null;
  linked_pfd_streams?: PnidLinkedPfdStreamRef[] | null;
  segments_review_status?: SegmentsReviewStatus | null;
  label_bbox?: BoundingBox | null;
}

export interface PnidNodeUpdate {
  node_no?: number;
  source_ref?: string;
  source_pnid?: string;
  to?: string | null;
  dest_ref?: string;
  dest_pnid?: string;
  line_no?: string;
  note?: string | null;
  line_type?: LineType;
  instrument_line_subtype?: InstrumentLineSubtype | null;
  installation_type?: InstallationType | null;
  is_jacketed?: boolean;
  tracing_type?: TracingType | null;
  flow_profile?: FlowProfile | null;
  slope_ratio?: string | null;
  is_two_phase?: boolean;
  flow_direction?: FlowDirection | null;
  primary_direction?: PrimaryDirection | null;
  drain_type?: string | null;
  painting_spec?: string | null;
  is_duct?: boolean;
  duct_bbox?: BoundingBox | null;
  polyline?: number[] | null;
  status?: MarkingStatus;
  line_spec?: Record<string, any>;
  notes?: string | null;
  parent_node_id?: string | null;
  branch_id?: string | null;
  is_bifurcation_point?: boolean;
  line_segments?: PnidNodeLineSegment[] | null;
  linked_pfd_streams?: PnidLinkedPfdStreamRef[] | null;
  segments_review_status?: SegmentsReviewStatus | null;
  label_bbox?: BoundingBox | null;
}

// === AI Data Types ===

export interface AIEquipmentInScope {
  tag: string;
  name: string;
  type: string;
  details: Record<string, any>;
  instruments: string[];
}

export interface AILine {
  raw: string;
  size_in?: string | null;
  service_code?: string | null;
  sequence_no?: string | null;
  pipe_class?: string | null;
  remark?: string | null;
}

export interface AIInstrument {
  tag: string;
  prefix: string;
  associated_equipment_tag?: string | null;
  loop_id?: string | null;
}

export interface AITieIn {
  tie_in_id: string;
  direction: string;
  from?: string | null;
  from_equipment_tag?: string | null;
  to_equipment_tag?: string | null;
  service?: string | null;
}

export interface AIControlLoop {
  loop_id: string;
  elements: string[];
}

export interface PnidAIData {
  pid: { number: string; title: string };
  page_image?: {
    width_px: number;
    height_px: number;
    render_scale?: number;
    dpi?: number | null;
    coordinate_system?: string;
  };
  line_segments?: {
    id: string;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    kind?: string | null;
    confidence?: number | null;
  }[];
  equipment_in_scope: AIEquipmentInScope[];
  external_equipment_refs: {
    tag: string;
    reason: string;
    pnid_name?: string | null;
    source_ref?: string | null;
    source_pnid?: string | null;
    dest_ref?: string | null;
    dest_pnid?: string | null;
    line_no?: string | null;
    stream_no?: string | null;
    direction?: string | null;
    external_ref?: string | null;
    external_pnid?: string | null;
    external_drawing?: string | null;
    external_equipment?: string | null;
    local_ref?: string | null;
    service?: string | null;
    context?: string | null;
  }[];
  lines: AILine[];
  tie_ins: AITieIn[];
  instruments: AIInstrument[];
  control_loops: AIControlLoop[];
  notes: string[];
  source: { pdf_filename?: string; extraction_version?: string };
}

// === Tag Categories ===
// Visual organization of P&ID entities for better UX

export type TagCategory = 'pnid' | 'line' | 'equipment' | 'instrument' | 'drain';

export interface TagCategoryInfo {
  name: string;
  icon: string;
  color: string;       // Primary color for badges
  bgColor: string;     // Background color for highlights
  borderColor: string; // Border color for UI elements
}

export const TAG_CATEGORIES: Record<TagCategory, TagCategoryInfo> = {
  pnid: {
    name: 'P&ID',
    icon: '📋',
    color: '#6366f1',      // Indigo
    bgColor: '#eef2ff',
    borderColor: '#c7d2fe',
  },
  line: {
    name: 'Line',
    icon: '➡️',
    color: '#0891b2',      // Cyan
    bgColor: '#ecfeff',
    borderColor: '#a5f3fc',
  },
  equipment: {
    name: 'Equipment',
    icon: '⚙️',
    color: '#059669',      // Emerald
    bgColor: '#ecfdf5',
    borderColor: '#a7f3d0',
  },
  instrument: {
    name: 'Instrument',
    icon: '📊',
    color: '#d97706',      // Amber
    bgColor: '#fffbeb',
    borderColor: '#fde68a',
  },
  drain: {
    name: 'Drain',
    icon: '🔽',
    color: '#dc2626',      // Red
    bgColor: '#fef2f2',
    borderColor: '#fecaca',
  },
};

// Categorization helpers

export function getCategoryForEquipment(_eq: PnidEquipment): TagCategory {
  return 'equipment';
}

export function getCategoryForNode(node: PnidNode): TagCategory {
  // Check if destination is a drain
  if (node.drain_type || isDrainDestination(node.dest_ref)) {
    return 'drain';
  }
  return 'line';
}

export function getCategoryForInstrumentType(_instrumentType: EquipmentInstrumentType): TagCategory {
  void _instrumentType;
  return 'instrument';
}

export function getCategoryInfo(category: TagCategory): TagCategoryInfo {
  return TAG_CATEGORIES[category];
}

// Get category badge styles for inline use
export function getCategoryBadgeStyle(category: TagCategory): Record<string, string | number> {
  const info = TAG_CATEGORIES[category];
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 10,
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: 4,
    color: info.color,
    backgroundColor: info.bgColor,
    border: `1px solid ${info.borderColor}`,
  };
}

// === Canvas Comments ===
// User feedback comments stored separately from annotation data

export interface CanvasComment {
  id: string;
  x: number;
  y: number;
  text: string;
  author: string;
  created_at: string;
  resolved: boolean;
  resolved_at?: string | null;
  resolved_by?: string | null;
}

export interface CanvasCommentCreate {
  x: number;
  y: number;
  text: string;
  author: string;
}

export interface CanvasCommentUpdate {
  text?: string;
}

// === Unified Instrument Model ===
// Matches backend PnidInstrument (pnid_schema.py:502-532)

export interface MarkingProvenance {
  source: string; // 'template_match' | 'manual' | 'ai_extraction' | 'ml_detection'
  metadata: Record<string, any>;
}

export type InstrumentCoreFieldValue = string | string[] | boolean | null;
export type InstrumentCoreFields = Record<string, InstrumentCoreFieldValue>;
export type InstrumentTagStatus = 'tagged' | 'untagged';

export interface PnidInstrument {
  id: string;
  context_type: 'equipment' | 'node' | 'instrument' | null;
  context_id: string | null;
  instrument_text: string | null;
  instrument_type: string | null;
  subtype: string | null;
  contextual_type: ContextualValveType | null;
  isolation_valve_tags: string[];
  linked_instruments: PnidLinkedInstrumentLink[];
  pnid_hyperlink: PnidHyperlinkRef | null;
  notes: string | null;
  /** The reviewer's plain-language name for the instrument. Null when none is set. */
  description: string | null;
  core_fields: InstrumentCoreFields;
  /**
   * Every region the SME drew for this instrument, in draw order. Region 0 is the
   * symbol, and the rest are the balloon and any captions worth reading. This is the
   * geometry the SME owns; `bbox` is derived from it.
   */
  bboxes?: BoundingBox[];
  /**
   * The first region, written by the backend on every save. Every view that frames,
   * zooms to, or tests one box for this instrument reads it, and it is the anchor the
   * distance rules measure from.
   */
  bbox: BoundingBox | null;
  status: string; // 'unverified' | 'ai_identified' | 'in_progress' | 'final' | 'skipped'
  tag_status: InstrumentTagStatus;
  provenance: MarkingProvenance | null;
  created_at: string | null;
  generated_id: string | null;
  // PSV-specific (equipment context)
  psv_line_us: string | null;
  psv_reducer_us: string | null;
  psv_line_ds: string | null;
  psv_expander_ds: string | null;
  // Node-specific
  branch_ref: string | null;
  branch_direction: BranchDirection | null;
  sequence_order: number | null;
  present_on_node: boolean;
  /**
   * The plant domain a reviewer named as this asset's owner. Empty means the
   * drawing that draws the asset owns it.
   */
  owner_domain_id: string;
}

export interface PnidInstrumentUpdate {
  context_type?: 'equipment' | 'node' | 'instrument' | null;
  context_id?: string | null;
  instrument_text?: string | null;
  instrument_type?: string | null;
  subtype?: string | null;
  contextual_type?: ContextualValveType | null;
  isolation_valve_tags?: string[];
  linked_instruments?: PnidLinkedInstrumentLink[];
  pnid_hyperlink?: PnidHyperlinkRef | null;
  notes?: string | null;
  /** Null or blank clears the description; absent leaves it alone. */
  description?: string | null;
  core_fields?: InstrumentCoreFields;
  /** The whole region list, replacing the stored one. The backend re-derives `bbox`. */
  bboxes?: BoundingBox[];
  status?: string;
  tag_status?: InstrumentTagStatus;
  /** Whether this drawing is the one that defines the tag. Absent leaves the
   *  stored mark alone; the server releases the tag's other marks when it is
   *  set true. */
  is_primary_source?: boolean | null;
  generated_id?: string | null;
  psv_line_us?: string | null;
  psv_reducer_us?: string | null;
  psv_line_ds?: string | null;
  psv_expander_ds?: string | null;
  branch_ref?: string | null;
  branch_direction?: BranchDirection | null;
  sequence_order?: number | null;
  present_on_node?: boolean;
  /**
   * The plant domain a reviewer named as this asset's owner. Empty means the
   * drawing that draws the asset owns it; absent says nothing about ownership.
   */
  owner_domain_id?: string;
}

/**
 * Every region of an instrument, in draw order, with region 0 the symbol.
 *
 * A marking made before multi-box shipped carries only the singular `bbox`, and the
 * backend's read shim turns it into a list of one on the way out — but a document
 * served by a backend that predates the shim still arrives singular, so the list is
 * reconstructed here rather than assumed.
 */
export function instrumentRegions(
  instrument: Pick<PnidInstrument, 'bbox' | 'bboxes'>,
): BoundingBox[] {
  if (instrument.bboxes && instrument.bboxes.length > 0) return instrument.bboxes;
  return instrument.bbox ? [instrument.bbox] : [];
}

// === Click targets (GET /documents/{docId}/pnid/click-targets) ===
// Boxes on the sheet an SME can click to mark an instrument instead of dragging one.
// A generator writes them for the whole sheet; the canvas only reads them.

export type ClickTargetKind = 'text' | 'symbol';

/** Unique within one generator run. A regeneration issues new ids. */
export type ClickTargetId = string;

export interface ClickTarget {
  id: ClickTargetId;
  kind: ClickTargetKind;
  /** Sheet pixels, the space an instrument's regions are saved in. */
  bbox: BoundingBox;
  /** The generator's confidence. Display only. */
  score: number;
  /** A text target's rows, joined with `\n` in reading order. */
  text?: string;
}

/** The read's response body: one generator run, or `null` for a drawing never generated. */
export interface ClickTargetColumn {
  generator: string;
  generated_at: string;
  targets: ClickTarget[];
}

// === Asset identification (POST /documents/{docId}/pnid/identify) ===
// The route reads the sheet, crops each region's box and runs the tag rules over what it
// finds there. It answers with a suggestion and writes nothing, so a caller is free to
// ask again as often as the SME edits.

/** What the module decided the drawn box is. */
export type IdentifyAssetKind = 'instrument' | 'equipment' | 'node';

/** The ladder that produced a suggestion, cheapest first. */
export type IdentifyRung = 'tag_rules' | 'vlm';

/**
 * One region of a marking and where it came from. A click target is sent by id, and the
 * backend crops the box it stores for that id; any other region sends its own box.
 */
export type IdentifyRegion =
  | { kind: 'click_target'; id: ClickTargetId }
  | { kind: 'drawn'; bbox: BoundingBox };

export interface IdentifyAssetRequest {
  /**
   * Every region of the marking, in draw order. The backend reads these regions and
   * nothing else, so text outside them is not read. Never empty; `regions[0]` is the
   * symbol, and the distance rules measure from it.
   */
  regions: IdentifyRegion[];
  /** Dialog values as they stand on a re-run. */
  existing_fields?: Record<string, unknown> | null;
  /**
   * Storage keys the SME has edited by hand. The backend overlays their non-empty
   * values on its own answer and never overwrites them; every other key it derives
   * fresh. Absent on the first call, where nothing has been edited yet.
   */
  touched_fields?: string[] | null;
}

export interface IdentifyAssetResponse {
  kind: IdentifyAssetKind;
  /**
   * The whole suggestion, keyed by schema storage key (`instrument_text`,
   * `instrument_type`, `subtype`, ...). It supersedes the previous answer rather
   * than adding to it, so a key the backend omits has no suggested value: the
   * caller clears that field unless the SME owns it.
   */
  fields: Record<string, unknown>;
  /**
   * The lines the sheet was read as, joined by newline, for debugging. Empty when
   * the backend answered without reading the sheet again.
   */
  ocr_text: string;
  rung: IdentifyRung;
}

// ---------- Review policy (pnid_review_policy.md) ----------

export type ReviewPolicySeverity = 'BLOCKER' | 'FLAG' | 'ADVISORY';

export type ReviewPolicyTargetKind =
  | 'document'
  | 'equipment'
  | 'node'
  | 'instrument'
  | 'relationship'
  | 'loop';

export interface ReviewPolicyFinding {
  rule: string;
  severity: ReviewPolicySeverity;
  target_kind: ReviewPolicyTargetKind;
  target_id: string | null;
  target_ref: string | null;
  message: string;
}

export interface ReviewPolicySummary {
  policy_version: string;
  total: number;
  blockers: number;
  flags: number;
  advisories: number;
  by_rule: Record<string, number>;
}

export interface ReviewPolicyReport {
  doc_id: string;
  pnid_no?: string | null;
  summary: ReviewPolicySummary;
  findings: ReviewPolicyFinding[];
}

export interface PolicyRuleConfig {
  severity: ReviewPolicySeverity;
  enabled: boolean;
  title: string;
  signoff_only?: boolean;
  description?: string;
  fires_when?: string;
  example?: string;
}

export interface PolicyRuleCounts {
  domain_id: string;
  policy_db_version?: number | null;
  documents_evaluated: number;
  rule_counts: Record<string, number>;
  rule_docs: Record<string, number>;
}

export interface PolicyRequiredField {
  rule: string;
  field: string;
  message?: string;
}

export interface PolicyEquipmentMatrixRow {
  type_pattern: string;
  groups: Array<{ name: string; aliases: string[] }>;
}

export interface ReviewPolicyDoc {
  meta: {
    policy_semver?: string;
    db_version?: number;
    status?: string;
    description?: string;
  };
  rules: Record<string, PolicyRuleConfig>;
  instrument_required_fields: Record<string, PolicyRequiredField[]>;
  equipment_design_matrix: PolicyEquipmentMatrixRow[];
  interlock: { logic_subtypes: string[]; actionable_target_types: string[] };
}

export interface PolicyChange {
  kind: string;
  rule?: string;
  field?: string;
  instrument_type?: string;
  before?: unknown;
  after?: unknown;
}

export interface PolicyVersionMeta {
  id: string;
  version_no: number;
  status: string;
  change_summary?: string | null;
  created_by: string;
  created_at?: string | null;
  updated_by?: string | null;
  updated_at?: string | null;
  published_by?: string | null;
  published_at?: string | null;
}

export interface PolicyDraftEvent {
  at: string;
  by: string;
  source: 'manual' | 'agent' | 'restore';
  summary?: string | null;
  changes: PolicyChange[];
}

export interface ReviewPolicyOverview {
  published: ReviewPolicyDoc;
  draft: ReviewPolicyDoc | null;
  draft_events: PolicyDraftEvent[];
  versions: PolicyVersionMeta[];
  can_publish: boolean;
  changes: PolicyChange[];
}

export interface PolicyDiffResponse {
  base: string;
  compare: string;
  base_policy: ReviewPolicyDoc;
  compare_policy: ReviewPolicyDoc;
  changes: PolicyChange[];
}

export interface PolicyImpactDocRow {
  doc_id: string;
  pnid_no?: string | null;
  base: { total: number; blockers: number; flags: number; advisories: number };
  compare: { total: number; blockers: number; flags: number; advisories: number };
  delta: { total: number; blockers: number; flags: number; advisories: number };
  rule_deltas: Record<string, number>;
  new_examples?: ReviewPolicyFinding[];
  removed_examples?: ReviewPolicyFinding[];
}

export interface PolicyImpactReport {
  domain_id: string;
  base_version?: number | null;
  compare_version?: number | null;
  include_ai: boolean;
  docs: PolicyImpactDocRow[];
  totals: {
    base: { blockers: number; flags: number; advisories: number };
    compare: { blockers: number; flags: number; advisories: number };
  };
  rule_totals: Record<string, number>;
  changes: PolicyChange[];
}

export interface PolicyAgentChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface PolicyAgentChatResponse {
  reply: string;
  model?: string;
  summary?: string;
  ops?: Array<Record<string, unknown>>;
  proposed_policy?: ReviewPolicyDoc;
  changes?: PolicyChange[];
  proposal_error?: string;
}

// ---------- Register context (what the plant register knows about this drawing's assets) ----------

/** One drawing an asset appears on, and how to open the asset there. */
export interface RegisterAppearance {
  source_file_id: string;
  /** The document this drawing is opened by, null when it has no live annotation. */
  doc_id: string | null;
  drawing_no: string | null;
  /** The element this drawing draws the asset as, null when only evidence names it. */
  equipment_id: string | null;
  is_primary: boolean;
}

/**
 * One equipment tag's standing in the register: its canonical fields and where
 * it appears.
 *
 * `asset_id` and the canonical fields are null only for a tag the register has
 * no asset for, which the tag lookup can answer for a tag a reviewer has just
 * typed. Every entry the drawing-wide context lists resolved to an asset.
 */
export interface RegisterEquipmentContext {
  asset_id: string | null;
  tag: string;
  asset_class: string | null;
  asset_type: string | null;
  display_name: string | null;
  appearances: RegisterAppearance[];
  /**
   * The drawing a reviewer approved as primary for the asset, or null while no
   * drawing is — in which case publishing writes nothing for it.
   */
  primary_source_file_id: string | null;
  this_file_is_primary: boolean;
  /**
   * The mark this drawing's own annotation holds for the entry — what its
   * Primary P&ID checkbox states — or null when this drawing draws no element
   * for the entry, as a tag a reviewer has only just typed. Every fetch carries
   * it fresh, so a panel whose mark another drawing took over shows the release
   * without a page reload.
   */
  this_drawing_mark: boolean | null;
}

/** Keyed by equipment element id. Equipment with no register asset is absent. */
export interface RegisterContext {
  source_file_id: string;
  equipments: Record<string, RegisterEquipmentContext>;
}

// ---------- Tag suggestions (tags the drawing's domain already uses) ----------

/** Which tag vocabulary a suggestion lookup searches. */
export type TagSuggestionKind = 'equipment' | 'instrument';

/** One tag the domain already uses, offered while a reviewer types. */
export interface TagSuggestion {
  tag: string;
  description: string;
  /** How many of the domain's drawings carry this tag, which is what orders equal matches. */
  drawing_count: number;
  /** The drawing numbers that carry this tag, sorted; a drawing with no number is counted but not listed. */
  drawings: string[];
  /** The typed text is this exact tag once separators are normalized. */
  normalized_match: boolean;
}

/** One line number the plant's drawings already carry, offered while an SME types Line Number. */
export interface LineSuggestion {
  line_no: string;
  /** How many of the plant's drawings carry it: the length of `drawings`. */
  drawing_count: number;
  /** The drawing numbers that carry it, sorted, the current drawing among them. */
  drawings: string[];
}

// ---------- Redundancy sets (interchangeable units of one plant) ----------

/** Interchangeable units in one service, by normalized tag. A tag is in at most
 *  one set of its plant, and a set holds at least two. */
export interface RedundancySet {
  id: string;
  tags: string[];
}

/** The 409 body of a PUT whose tags span more than one set. */
export interface RedundancySetConflict {
  code: 'redundancy_set_conflict';
  message: string;
  sets: RedundancySet[];
}

// ---------- Plant register assets and their documents ----------

/** One row of `GET /plant-assets`, reduced to the field the frontend reads. */
export interface PlantRegisterAsset {
  id: string;
}

/** The register assets that carry one tag in one plant. A tag that several assets
 *  carry names no single asset, so it has no documents to list. */
export type PlantAssetMatch =
  | { match: 'none' }
  | { match: 'one'; asset: PlantRegisterAsset }
  | { match: 'several' };

/** One document linked to a register asset, as `GET /plant-assets/{asset_id}/documents`
 *  lists it, reduced to the fields the frontend reads. */
export interface AssetDocument {
  /** Empty for a plant-common document, which has no evidence row of its own. */
  evidence_id: string;
  source_file_id: string;
  file_name: string;
  /** Empty when the document has no title of its own; show `file_name` then. */
  display_title: string;
  /** The file's category, such as `Datasheet` or `SOP`; empty when the file has none. */
  category: string;
  /** The copilot web app's page for the document. */
  open_url: string;
  /** True for a plant-common document, which every asset of the plant shares. */
  is_common: boolean;
}

// ---------- Legend sheets (one vendor's P&ID legend, per plant) ----------

/** The line number convention a legend sheet defines, written for the stored
 *  `line_no` text: an anchored regex with named groups, one example that matches
 *  it, and the layout as the sheet draws it. */
export interface LineNumberFormat {
  pattern: string;
  example: string;
  description: string;
}

/** The instrument tag convention a legend sheet defines: an anchored regex with the
 *  named groups `function` and `loop_number` (and optionally `area`,
 *  `suffix_separator`, `suffix`), one example that matches it, and the template that
 *  renders a parsed tag as its stored text. */
export interface TagFormat {
  pattern: string;
  example: string;
  template: string;
  description: string;
}

/** One uploaded legend sheet file; `url` is where it is served from. */
export interface LegendSheetFile {
  name: string;
  url: string;
}

/** One edited crop of a legend symbol. `url` serves the PNG; `source` is where it
 *  was cut from, the file by its index in the sheet's `files`, the 1-based page and
 *  the box in PDF points, kept as provenance and rendered from nowhere. */
export interface LegendExemplarVariant {
  url: string;
  dpi: number;
  source: { file_index: number; page: number; bbox: BoundingBox } | null;
}

/** A legend symbol: its caption as the sheet prints it, the instrument form values a
 *  match implies (keyed by `storage_key`) and one or more crops of the symbol. */
export interface LegendExemplar {
  id: string;
  caption: string;
  implies: Record<string, string | boolean>;
  variants: LegendExemplarVariant[];
}

/** A named set of legend files, the exemplars cut from them and its optional line
 *  number and tag formats, scoped to a plant. */
export interface LegendSheet {
  id: string;
  name: string;
  files: LegendSheetFile[];
  line_number_format: LineNumberFormat | null;
  tag_format: TagFormat | null;
  exemplars: LegendExemplar[];
}

// ---------- Publication (publish the working copy as a drawing version) ----------

/** What publishing does to one plant-register asset. */
export type AssetImpactAction =
  | 'create'
  | 'update'
  /** The asset's canonical fields already match the drawing; only evidence refreshes. */
  | 'unchanged'
  /** Another drawing is primary for the asset, so this one only records an appearance. */
  | 'evidence_only'
  /** No drawing is primary for the asset, so publishing writes nothing for it. */
  | 'skip_ambiguous_primary'
  | 'skip_tag_collision'
  | 'skip_empty_tag'
  | 'skip_class_conflict'
  | 'skip_shared_asset'
  /** Two candidates on this drawing claim the tag and neither can be chosen. */
  | 'skip_ambiguous_candidates';

/** Canonical asset fields are text; `old` is null on a create, '' when unset. */
export interface AssetImpactChange {
  field: string;
  old: string | null;
  new: string | null;
}

/** A drawing a tag appears on in a publish plan. */
export interface AssetImpactFile {
  source_file_id: string;
  drawing_no: string | null;
  is_primary?: boolean;
}

export interface AssetImpact {
  tag: string;
  action: AssetImpactAction;
  /** Updates carry only the fields that differ; creates carry the initial values. */
  changes: AssetImpactChange[];
  /** Populated on skip_ambiguous_primary: the other P&IDs that show this asset. */
  files: AssetImpactFile[];
  /** Set when one of this tag's sheets clashed. A plant row shows the mildest
   *  action of the sheets it covers, so the action alone does not say so. */
  blocking?: boolean;
}

/** The parts of the projected document the UI reads: its identity and its verdict. */
export interface PublicationDocumentIdentity {
  document_number?: string | null;
  document_title?: string | null;
  /** The resolver's verdict on this candidate. A candidate that loads at all has
   *  no blocking conflict left, so only its warnings still have something to say. */
  validation?: PublicationValidation | null;
}

export interface PublicationCandidateData {
  document?: PublicationDocumentIdentity | null;
}

export interface PublicationCandidate {
  source_file_id: string;
  source_annotation_id: string;
  expected_updated_at: string;
  data: PublicationCandidateData;
  data_sha256: string;
  /** Highest published version, or null when this would be the first publication. */
  latest_version: number | null;
  /** Ordered plant-register impact, one entry per asset. Empty for PFD drawings. */
  asset_impacts: AssetImpact[];
  /** Set when the drawing writes no plant-register rows, e.g. a PFD publication. */
  note?: string;
  /** Set when the assets below will be written but not grouped into the tree. */
  hierarchy_note?: string;
}

/** One blocking or resolved validation conflict from a `publication_blocked` response. */
export interface PublicationConflict {
  code: string;
  field: string;
  message: string;
  blocking: boolean;
  resolved: boolean;
}

/**
 * One thing the resolver wants said about a publication it is letting through.
 * A warning never blocks, so it carries no blocking/resolved state, and it names
 * a field only when it is about one.
 */
export interface PublicationWarning {
  code: string;
  field?: string | null;
  message: string;
}

export interface PublicationValidation {
  conflicts: PublicationConflict[];
  warnings: PublicationWarning[];
}

export interface PublishResponse {
  id: string;
  version_number: number;
  published_at: string;
  published_by: string;
  /** False when the working copy was already published unchanged (no-op). */
  created: boolean;
}

/** Error body shape for publication failures — `detail` is an object, never a string. */
export interface PublicationErrorDetail {
  code: string;
  message?: string;
  validation?: PublicationValidation;
  /** Present on `working_copy_stale`: the working copy's real updated_at. */
  current_updated_at?: string;
  /** Present on `plant_publication_blocked`: per-drawing failures. */
  drawings?: Array<{
    doc_id: string;
    drawing_no?: string | null;
    code?: string | null;
    message?: string | null;
    validation?: PublicationValidation | null;
  }>;
  expected?: string[];
  received?: string[];
}

export interface PublicationProjectionSummary {
  assets_inserted: number;
  assets_updated: number;
  evidence_inserted: number;
  evidence_upserted: number;
  hierarchy_written: number;
  /** Assets whose customer class keys (template.overview) this publish sets. */
  equipment_class_written: number;
  note?: string;
  hierarchy_note?: string;
  asset_impact_counts?: Record<string, number>;
}

export interface PlantDrawingPublicationCandidate {
  doc_id: string;
  source_file_id: string;
  source_annotation_id: string;
  drawing_no?: string | null;
  expected_updated_at: string;
  data_sha256: string;
  latest_version?: number | null;
  would_create: boolean;
  next_version: number;
  blocked: boolean;
  block_code?: string | null;
  block_message?: string | null;
  validation?: PublicationValidation | null;
  warnings: PublicationWarning[];
}

export interface PlantPublicationCandidate {
  plant: string;
  drawings: PlantDrawingPublicationCandidate[];
  asset_impacts: AssetImpact[];
  summary: PublicationProjectionSummary;
  note?: string;
  hierarchy_note?: string;
  blocked: boolean;
}

export interface PlantPublishPlanResponse {
  plant: string;
  drawings: PlantDrawingPublicationCandidate[];
  would_create_any: boolean;
  note?: string;
  hierarchy_note?: string;
  summary: PublicationProjectionSummary;
  asset_impacts: AssetImpact[];
  no_action: Array<Record<string, unknown>>;
  blocked: boolean;
}

export interface PlantPublishResponse {
  plant: string;
  drawings: Array<{
    doc_id: string;
    source_file_id: string;
    version_number: number;
    created: boolean;
  }>;
  projection: PublicationProjectionSummary;
  asset_impacts: AssetImpact[];
}

/** One domain the plant listing offers, with what the picker needs to place it.
 *  `plant` carries the domain's display name; `domain_id` is what the plant
 *  routes are addressed by. */
/** One element the agent wrote that no SME has saved since, as
 *  `GET /plants/{domain_id}/agent-review-queue` lists it. `href` opens the drawing on
 *  that element: the editor for an instrument or equipment, the canvas centred
 *  on a line. */
export type MarkingKind = 'instrument' | 'node' | 'equipment';

/** Whether the agent created the element or modified an existing one. */
export type AgentReviewChange = 'create' | 'modify';

export interface AgentReviewQueueItem {
  kind: MarkingKind;
  id: string;
  label: string;
  href: string;
  change: AgentReviewChange;
  /** The filter facets after the kind: an instrument's or equipment's type, a
   *  line's type; the subtype where the element has one. */
  element_type: string | null;
  subtype: string | null;
}

export interface AgentReviewQueueDrawing {
  doc_id: string;
  pnid_no: string;
  title: string;
  items: AgentReviewQueueItem[];
}

export interface AgentReviewQueue {
  domain_id: string;
  pending_count: number;
  drawings: AgentReviewQueueDrawing[];
}

export interface PlantSummary {
  plant: string;
  domain_id: string;
  /** The domain above this one, empty for a top-level domain. The picker heads
   *  a group with `parent`, which a parent holding no drawings of its own
   *  cannot supply itself — it never appears in this listing. */
  parent: string;
  parent_id: string;
  drawings: number;
  /** Whether any of those drawings drew an equipment element or an instrument.
   *  False means the plant page would open empty, so the picker offers the
   *  domain without letting it be chosen. */
  has_assets: boolean;
}

/** One drawing an asset tag was found on. */
export interface PlantAssetAppearance {
  doc_id: string;
  drawing_no: string | null;
  drawing_name: string;
  /** The drawing's own element the asset is read from, and what a write
   *  against this appearance addresses. */
  element_id: string;
  is_primary: boolean;
  /** True when the register's evidence links this drawing to the asset. */
  is_evidenced: boolean;
}

/** The register's published values for one tag.
 *
 *  A field is null when the register has not published it — no register row for
 *  the tag yet, or a row carrying nothing at that level: an equipment type
 *  outside the reviewed taxonomy has no group, and a row whose sub-type the
 *  reviewer left blank has no sub-type. Both labels are the register's own
 *  wording: the page cannot resolve the register's legacy alias spellings
 *  without the taxonomy's alias table, so they are never compared against the
 *  live values, only stated. */
export interface PlantAssetPublished {
  asset_group_label: string | null;
  asset_subcategory_label: string | null;
  display_name: string | null;
}

/** The single drawing element an edit on this page writes to. */
export interface PlantAssetEditTarget {
  doc_id: string;
  element_id: string;
}

/** Which kind of drawing element an asset is read from, and so which endpoint a
 *  write against it addresses and which columns of the register apply to it. */
export type PlantAssetFamily = 'equipment' | 'instrument';

export interface PlantAsset {
  normalized_tag: string;
  tag: string;
  /** `equipment` or `instrument`: the kind of drawing element this row is read
   *  from, and so the endpoint an edit on it writes to. */
  asset_family: PlantAssetFamily;
  /** The reviewed type key for equipment, the stored instrument type for an
   *  instrument. */
  asset_type: string;
  /** The sub-type as the reviewer selected it: a label from the equipment
   *  vocabulary under `asset_type`, or an instrument sub-type. Empty when nobody
   *  has picked one. */
  subtype: string;
  /** Empty for an instrument, which carries no description. */
  description: string;
  /** True when the tag holds several assets, e.g. `104-JA/JB`. */
  compound: boolean;
  appearances: PlantAssetAppearance[];
  /** Null when the tag appears on several drawings and none is primary. */
  edit_target: PlantAssetEditTarget | null;
  /** The same tag as the register published it, for showing beside the live
   *  values. Absent when the backend serving this response predates the field. */
  published?: PlantAssetPublished;
}

/** One family's group of the plant's assets. Buckets arrive in display order. */
export interface PlantHierarchyBucket {
  /** Qualified by the family that owns the group: `equipment.pumps`,
   *  `instrument.safety_valves`. One flat bucket list is one flat key
   *  namespace, and a group is addressed by this key alone. */
  key: string;
  family: PlantAssetFamily;
  /** The bare group name the rail shows. */
  label: string;
  /** Whether a plant publish writes this bucket's assets to the register. False
   *  for `instrument.other` alone, whose assets are listed for an SME to correct
   *  their type. */
  publishable: boolean;
  assets: PlantAsset[];
}

export interface PlantHierarchy {
  /** The domain's display name; the request addressed the domain by id. */
  plant: string;
  buckets: PlantHierarchyBucket[];
}
